import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import type { FacilityContact } from '../../../../../js/api';
import { HealthcareEntitiesService } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import {
  BulkActionBar,
  BulkActionButton,
  RowSelectCheckbox,
} from '../../../components/bulk-action-bar';
import { ConfirmationDialog } from '../../../components/confirmation-dialog';
import { DrawerSectionHeader } from '../../../components/drawer-components';
import { DropdownSelect } from '../../../components/dropdown-select';
import { useActingAsEntity } from '../../../hooks/useActingAsEntity';
import {
  useCodeDefinitionsAsMap,
  useCodeDefinitionsAsOptions,
} from '../../../hooks/useCodeDefinitions';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { useRowSelection } from '../../../hooks/useRowSelection';
import { isRowSelectable } from '../../../utils/bulkSelection';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { getReactSelectCompactStyles } from '../../../utils/utils';
import { pluralize } from './contactDisplay';
import { LINK_DELETE_PERMISSION, LINK_UPDATE_PERMISSION } from './contactPermissions';
import { NotificationToggle } from './NotificationToggle';
import { contactQueryKeys, invalidateContactQueries } from './queryKeys';

const ROLE_CATEGORY = 'FacilityContactRoles';
// The API caps page size well above any real person's facility count.
const LINKS_LIMIT = 500;
// Memoized once: react-select re-styles whenever `styles` changes identity.
const COMPACT_STYLES = getReactSelectCompactStyles();
const ROLE_FILTER_STYLES = {
  ...COMPACT_STYLES,
  // react-select's control wraps by default, which drops the clear/chevron
  // indicators onto a second line under a long role name.
  control: (...args: Parameters<typeof COMPACT_STYLES.control>) => ({
    ...COMPACT_STYLES.control(...args),
    flexWrap: 'nowrap' as const,
  }),
};

type PendingRemoval = { ids: string[]; label: string } | null;

/**
 * The contact drawer's facility links: role filter, per-link notification
 * toggle and remove, and checkbox bulk remove (the contact itself is kept).
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export const ContactFacilitiesSection: React.FC<{
  contactId: string;
  contactEntityId: string;
}> = ({ contactId, contactEntityId }) => {
  const queryClient = useQueryClient();
  const { actingAsEntity } = useActingAsEntity();
  const canUpdateLinks = useHasPermission(LINK_UPDATE_PERMISSION);
  const canRemoveLinks = useHasPermission(LINK_DELETE_PERMISSION);
  const { data: roleOptions } = useCodeDefinitionsAsOptions(ROLE_CATEGORY);
  const { data: roleMap } = useCodeDefinitionsAsMap(ROLE_CATEGORY);
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval>(null);

  // Links all belong to the contact's org; acting as another org locks them.
  const inScope = isRowSelectable(contactEntityId, actingAsEntity?.id);
  const selection = useRowSelection(`${contactId}|${roleFilter ?? ''}|${actingAsEntity?.id ?? ''}`);

  const contactIds = useMemo(() => [contactId], [contactId]);
  const { data, isLoading, isError } = useQuery({
    queryKey: contactQueryKeys.links(contactIds),
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilityContactsList({
        contact: contactIds,
        ordering: 'facility__name',
        limit: LINKS_LIMIT,
      }),
  });

  const links = useMemo(() => data?.results || [], [data]);
  const visibleLinks = useMemo(
    () => (roleFilter ? links.filter((link) => link.role === roleFilter) : links),
    [links, roleFilter]
  );
  const visibleIds = useMemo(() => visibleLinks.map((link) => link.id), [visibleLinks]);
  // One row per facility + role, so a person holding several roles at a
  // facility has more links than facilities (the list column counts facilities).
  const facilityCount = useMemo(() => new Set(links.map((link) => link.facility.id)).size, [links]);
  const facilitiesHeading =
    facilityCount === links.length
      ? `Facilities (${facilityCount})`
      : `Facilities (${facilityCount}) · ${pluralize(links.length, 'role')}`;
  const showSelection = (canRemoveLinks || canUpdateLinks) && inScope;
  const allSelected = selection.areAllSelected(visibleIds);
  const someSelected = !allSelected && visibleIds.some((id) => selection.selected.has(id));

  const notificationsMutation = useMutation({
    mutationFn: ({ id, receive }: { id: string; receive: boolean }) =>
      HealthcareEntitiesService.healthcareEntitiesFacilityContactsPartialUpdate({
        id,
        requestBody: { receive_notifications: receive },
      }),
    onSuccess: () => invalidateContactQueries(queryClient),
    onError: (error) => handleApiError(error, { messagePrefix: 'Failed to update notifications' }),
  });

  const bulkNotifyMutation = useMutation({
    mutationFn: (receive: boolean) =>
      HealthcareEntitiesService.healthcareEntitiesFacilityContactsBulkNotificationsCreate({
        requestBody: { ids: selection.selectedIds, receive_notifications: receive },
      }),
    onSuccess: async (result, receive) => {
      const unchanged = result.skipped
        ? ` · ${result.skipped} already ${receive ? 'on' : 'off'}`
        : '';
      toast.success(
        `Notifications ${receive ? 'on' : 'off'} for ${pluralize(result.processed, 'facility link')}${unchanged}`
      );
      selection.clear();
      await invalidateContactQueries(queryClient);
    },
    onError: (error) => handleApiError(error, { messagePrefix: 'Failed to update notifications' }),
  });

  const removeMutation = useMutation({
    mutationFn: (ids: string[]) =>
      HealthcareEntitiesService.healthcareEntitiesFacilityContactsBulkUnlinkCreate({
        requestBody: { ids },
      }),
    onSuccess: async (result) => {
      toast.success(`Removed ${pluralize(result.processed, 'facility link')}`);
      setPendingRemoval(null);
      selection.clear();
      await invalidateContactQueries(queryClient);
    },
    onError: (error) => handleApiError(error, { messagePrefix: 'Failed to remove facility links' }),
  });

  const roleLabel = (link: FacilityContact) => roleMap?.[link.role] || link.role || '--';

  const renderBody = () => {
    if (isLoading) {
      return <div className="tw:text-zinc-500 tw:py-2">Loading facilities...</div>;
    }
    if (isError) {
      return (
        <div className="tw:text-stoplight-danger tw:py-2" data-testid="contact-links-error">
          Couldn&apos;t load this contact&apos;s facilities.
        </div>
      );
    }
    if (visibleLinks.length === 0) {
      return (
        <div
          className="tw:rounded-lg tw:border tw:border-dashed tw:border-zinc-300 tw:p-4 tw:text-center tw:text-zinc-500"
          data-testid="contact-links-empty"
        >
          {roleFilter ? 'No facilities in this role.' : 'Not linked to any facilities.'}
        </div>
      );
    }
    return (
      <ul className="tw:!m-0 tw:!pl-0 tw:list-none tw:divide-y tw:divide-zinc-200 tw:rounded-lg tw:border tw:border-zinc-200">
        {showSelection && (
          <li className="tw:flex tw:items-center tw:gap-3 tw:bg-zinc-50 tw:px-3 tw:py-1.5 tw:text-xs tw:text-zinc-500">
            <RowSelectCheckbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={() => selection.setMany(visibleIds, !allSelected)}
              label={allSelected ? 'Deselect all facility links' : 'Select all facility links'}
              data-testid="contact-links-select-all"
            />
            {allSelected ? 'Deselect All' : 'Select All'}
          </li>
        )}
        {visibleLinks.map((link) => (
          <li
            key={link.id}
            className="tw:flex tw:items-center tw:gap-3 tw:px-3 tw:py-2"
            data-testid={`contact-link-${link.id}`}
          >
            {showSelection && (
              <RowSelectCheckbox
                checked={selection.selected.has(link.id)}
                onChange={() => selection.toggle(link.id)}
                label={`Select ${link.facility.name ?? 'facility'}`}
                data-testid={`contact-link-select-${link.id}`}
              />
            )}
            <div className="tw:min-w-0 tw:flex-1">
              <div className="tw:truncate tw:font-medium tw:text-zinc-800">
                {link.facility.name}
              </div>
              <div className="tw:truncate tw:text-xs tw:text-zinc-500">
                {roleLabel(link)}
                {getFacilityLocationDisplayValue(link.facility)
                  ? ` · ${getFacilityLocationDisplayValue(link.facility)}`
                  : ''}
              </div>
            </div>
            <NotificationToggle
              checked={link.receive_notifications ?? true}
              disabled={!canUpdateLinks || notificationsMutation.isLoading}
              onChange={(receive) => notificationsMutation.mutate({ id: link.id, receive })}
              ariaLabel={`Notifications for ${link.facility.name ?? 'facility'}`}
              data-testid={`contact-link-notify-${link.id}`}
            />
            {canRemoveLinks && (
              <button
                type="button"
                className="tw:p-1 tw:text-zinc-400 tw:hover:text-stoplight-danger"
                title="Remove from this facility"
                aria-label={`Remove from ${link.facility.name ?? 'facility'}`}
                onClick={() =>
                  setPendingRemoval({
                    ids: [link.id],
                    label: link.facility.name ?? 'this facility',
                  })
                }
                data-testid={`contact-link-remove-${link.id}`}
              >
                <i className="far fa-trash-can" />
              </button>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <section data-testid="contact-facilities">
      <DrawerSectionHeader
        text={facilitiesHeading}
        icon={<i className="fal fa-fw fa-hospital tw:text-brand-green-0" />}
      />
      {links.length > 0 && (
        <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2">
          <label
            htmlFor="contact-links-role-filter"
            className="tw:!mb-0 tw:text-xs tw:font-medium tw:text-zinc-600 tw:whitespace-nowrap"
          >
            Filter
          </label>
          {/* w-fit + nowrap control: size to the selected role instead of wrapping
              the clear/chevron under a long value. */}
          <div className="tw:w-fit tw:min-w-40">
            <DropdownSelect
              inputId="contact-links-role-filter"
              aria-label="Filter by role"
              allOptions={roleOptions}
              value={roleFilter}
              onChange={(option) =>
                setRoleFilter(option && !Array.isArray(option) ? option.value : null)
              }
              placeholder="All roles"
              isClearable
              isSearchable={false}
              menuPlacement="auto"
              styles={ROLE_FILTER_STYLES}
            />
          </div>
        </div>
      )}
      {renderBody()}
      {showSelection && (
        <BulkActionBar
          count={selection.count}
          busy={removeMutation.isLoading || bulkNotifyMutation.isLoading}
          onClear={selection.clear}
          data-testid="contact-links-bulk-bar"
        >
          {canUpdateLinks && (
            <>
              <BulkActionButton
                onClick={() => bulkNotifyMutation.mutate(true)}
                disabled={bulkNotifyMutation.isLoading}
                title="Turn notifications on"
                aria-label="Turn notifications on"
                data-testid="contact-links-bulk-notify-on"
              >
                <i className="far fa-bell tw:mr-1" aria-hidden="true" />
                On
              </BulkActionButton>
              <BulkActionButton
                onClick={() => bulkNotifyMutation.mutate(false)}
                disabled={bulkNotifyMutation.isLoading}
                title="Turn notifications off"
                aria-label="Turn notifications off"
                data-testid="contact-links-bulk-notify-off"
              >
                <i className="far fa-bell-slash tw:mr-1" aria-hidden="true" />
                Off
              </BulkActionButton>
            </>
          )}
          {canRemoveLinks && (
            <BulkActionButton
              onClick={() =>
                setPendingRemoval({
                  ids: selection.selectedIds,
                  label: pluralize(selection.count, 'facility', 'facilities'),
                })
              }
              data-testid="contact-links-bulk-remove"
            >
              Remove
            </BulkActionButton>
          )}
        </BulkActionBar>
      )}
      <ConfirmationDialog
        open={pendingRemoval !== null}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) removeMutation.mutate(pendingRemoval.ids);
        }}
        title="Remove facility links?"
        body={`This contact will be removed from ${pendingRemoval?.label ?? ''} and stop receiving its notifications. The contact itself is kept.`}
        confirmText="Remove"
        confirmVariant="danger"
        isLoading={removeMutation.isLoading}
      />
    </section>
  );
};
